import { Box } from "@mui/system";
import { GridFilterInputValueProps, SUBMIT_FILTER_STROKE_TIME } from "@mui/x-data-grid-premium";
import { DateRange, DateRangePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import React, { memo, useEffect, useRef, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";

const InputBetweenDates: React.FC<GridFilterInputValueProps> = memo((props) => {
    const { item, applyValue, focusElementRef = null } = props;
    const filterTimeout = useRef<any>();
    const boxRef = useRef<HTMLDivElement | null>(null);
    const [filterValueState, setFilterValueState] = useState<[string, string]>(item.value ?? "");

    useEffect(() => {
        if (boxRef?.current?.parentElement?.style) {
            const parentWidth = boxRef.current.parentElement.style.width;
            boxRef.current.parentElement.style.width = "unset";
            boxRef.current.parentElement.style.minWidth = parentWidth;
        }

        return () => {
            clearTimeout(filterTimeout.current);
        };
    }, []);

    useEffect(() => {
        const itemValue = item.value ?? [undefined, undefined];
        setFilterValueState(itemValue);
    }, [item.value]);

    const updateFilterValue = (lowerBound: string, upperBound: string) => {
        clearTimeout(filterTimeout.current);
        setFilterValueState([lowerBound, upperBound]);

        filterTimeout.current = setTimeout(() => {
            applyValue({ ...item, value: [lowerBound, upperBound] });
        }, SUBMIT_FILTER_STROKE_TIME);
    };

    const dateRangeOnChange = (value: DateRange<unknown>) => {
        const [lower, upper] = value;
        const lowerDate = lower ? DateFormatter.getUTCDate(lower as Dayjs) : "";
        const upperDate = upper ? DateFormatter.getUTCDate(upper as Dayjs) : "";
        updateFilterValue(lowerDate, upperDate);
    };

    const formatValues = (): [Dayjs | null, Dayjs | null] => {
        const [lower, upper] = filterValueState;
        const formattedLower = lower ? dayjs(lower) : null;
        const formattedUpper = upper ? dayjs(upper) : null;
        return [formattedLower, formattedUpper];
    };

    return (
        <Box
            sx={{
                display: "inline-flex",
                flexDirection: "row",
                alignItems: "end",
            }}
            ref={boxRef}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateRangePicker
                    slotProps={{
                        textField: ({ position }) => ({
                            label: position === "start" ? "From" : "To",
                            variant: "standard",
                        }),
                    }}
                    value={formatValues()}
                    onChange={dateRangeOnChange}
                />
            </LocalizationProvider>
        </Box>
    );
});

export default InputBetweenDates;
